/**
 * The search results dropdown used by header/Search.tsx,
 * displays search auto complete results.
 * Clicking any result row takes the user to the search page for that result.
 */
import React, { Fragment, useEffect, useRef } from 'react';
import styled from 'astroturf/react';

import AutoCompleteResultRow, { SearchResult } from './AutoCompleteResultRow';
import routes from 'sitewide-js/core_pre_webpack/routes';
import { MIN_AUTOCOMPLETE_LENGTH } from './SearchBar';

// darkened overlay is z-index 50
const Container = styled.div<{ show?: boolean }>`
  display: none;
  position: absolute;
  z-index: 60;
  top: 48px;
  left: 0px;
  flex-direction: column;
  background-color: transparent;
  width: 100%;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;

  &.show {
    display: flex;
  }
`;

export const ContentWrapper = styled.div`
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.1),
    0 1px 8px rgba(0, 0, 0, 0.15);
  position: relative;

  &:before {
    position: absolute;
    content: '';
    left: 50%;
    top: -10px;
    border-right: 9px solid transparent;
    border-bottom: 9px solid rgba(0, 0, 0, 0.15);
    border-left: 9px solid transparent;
  }

  &:after {
    position: absolute;
    top: -9px;
    left: 50%;
    content: '';
    border-right: 9px solid transparent;
    border-bottom: 9px solid #ffffff;
    border-left: 9px solid transparent;
  }
`;

export const ScrollableContent = styled.div`
  display: flex;
  flex-direction: column;
  max-height: calc(50vh - 48px);
  overflow: scroll;
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const Row = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  padding: 12px 0px;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
`;

// DESIGN: small screen sizes
const ZeroStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  color: #333;
  font-size: 14px;
  text-align: center;
`;

export const Spinner = styled.i`
  margin-right: 8px;
`;

type Props = {
  show: boolean;
  searchParams: { q: string } & Record<string, string>;
  results: Array<SearchResult>;
  loading: boolean;
  highlightedIndex: number;
};

const AutoCompleteResults = ({
  show,
  searchParams,
  results,
  loading,
  highlightedIndex,
}: Props) => {
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);

  useEffect(() => {
    if (highlightedIndex >= 0) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  const encodedParams = Object.entries(searchParams).reduce(
    (result, [key, value]) => {
      result[key] = encodeURIComponent(value);
      return result;
    },
    {} as Record<string, string>,
  );

  return (
    <Container show={show}>
      <ContentWrapper>
        {loading ? (
          <LoadingContainer>
            <AutoCompleteResultRow
              type={'Query'}
              display_name={searchParams.q}
              entity_url={routes.search_path(encodedParams)}
              highlighted={highlightedIndex === 0}
            />
            <Row>
              <Spinner className="fa fa-spinner fa-spin" />
              Searching...
            </Row>
          </LoadingContainer>
        ) : results.length === 0 &&
          searchParams.q.length < MIN_AUTOCOMPLETE_LENGTH ? (
          <ZeroStateContainer>
            Type to search for Papers, People,
            <br />
            Research Interests, Universities, Videos, and Courses
          </ZeroStateContainer>
        ) : (
          <Fragment>
            <ScrollableContent>
              <AutoCompleteResultRow
                ref={(el) => {
                  itemRefs.current[0] = el;
                }}
                type={'Query'}
                display_name={searchParams.q}
                entity_url={routes.search_path(encodedParams)}
                highlighted={highlightedIndex === 0}
              />
              {results != null &&
                results.map((result, index) => (
                  <AutoCompleteResultRow
                    ref={(el) => {
                      itemRefs.current[index + 1] = el;
                    }}
                    // @ts-expect-error ts-migrate(2339) FIXME: Property 'id' does not exist on type 'SearchResult... Remove this comment to see the full error message
                    key={`${result.type}${result.id}`}
                    {...result}
                    highlighted={highlightedIndex === index + 1}
                  />
                ))}
            </ScrollableContent>
          </Fragment>
        )}
      </ContentWrapper>
    </Container>
  );
};

export default AutoCompleteResults;
